import * as React from 'react';
import { I18n } from 'react-redux-i18n';

import { FieldSelectorOnChangeMultiple } from '@common/components/FieldSelector/field-selector.types';
import { SkipLogicCondition, SkipLogicConditionOperator } from '@common/components/skip-logic/skip-logic.types';
import { QuestionnaireSkipLogicConditionCreateDto } from '@common/dto/questionnaire/skip-logic/condition-create-dto';
import { generateId } from '@common/utils/common';
import { getUniqueFieldCombination } from '@common/utils/fields';
import { QuestionnaireSkipLogicConditionListDto } from '@common/dto/questionnaire/skip-logic/condition-list-dto';
import { FieldType } from '@common/dto/field/type';
import { QuestionnaireFieldAddDto } from '@common/dto/questionnaire/field/add-dto';
import { QuestionnaireFieldUpdateDto } from '@common/dto/questionnaire/field/update-dto';
import { QuestionnaireFieldListDto } from '@common/dto/questionnaire/field/list-dto';
import { useTextBlock } from '../../../questionnaire-designer/components/text-block/text-block.hook';
import { QuestionnaireConsentTabCreateDto, QuestionnaireConsentTabUpdateDto } from '@common/dto/questionnaire/consent-tab';
import { QuestionnaireConsentPageProps } from './page.types';
import { useFieldsWithTextBlock } from '../../../fields-with-text-blocks/fields-with-text-blocks.hooks';
import { UpdatedLocalTexts, UpdatedLocalFields, FieldInList } from '../../../fields-with-text-blocks/fields-with-text-blocks.types';
import { QuestionnaireTextBlockLocalDto } from '@modules/fhq-design-tool/dto/text-block-create-dto';

export const usePageController = (props: QuestionnaireConsentPageProps) => {
  const { cachedData, onChange, showErrorMessage, getFhq4QuestionnaireField } = props;
  const [fieldChooserOpened, setFieldChooserOpened] = React.useState(false);
  const [editingFieldUniqueCombination, setEditingFieldUniqueCombination] = React.useState<string | undefined>();
  const [familyPagesSkipLogicConditions, setFamilyPagesSkipLogicConditions] = React.useState<SkipLogicCondition[]>([]);

  const initializeData = () => {
    let skipLogicConditions: QuestionnaireSkipLogicConditionListDto[] = [];

    if (cachedData) {
      skipLogicConditions = cachedData.skipLogic || [];
    }
    
    setFamilyPagesSkipLogicConditions(skipLogicConditions.map((x) => ({ ...x, id: generateId() } as SkipLogicCondition)) || []);
  };

  React.useEffect(() => {
    initializeData();
  }, [cachedData?.skipLogic]);

  const fields = cachedData?.fields || [];
  const editingField = fields.find((x) => getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }) === editingFieldUniqueCombination);

  const processedFields = React.useMemo(() => {
    return fields
      .map((x): FieldInList => ({
        id: getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }),
        name: x.name,
        original: x,
      }))
      .sort((a, b) => a.original.order - b.original.order);
  }, [fields]);

  const { getCombinedItems } = useFieldsWithTextBlock<FieldInList, QuestionnaireTextBlockLocalDto>({ fields: processedFields, texts: (cachedData?.texts || []) as Array<QuestionnaireTextBlockLocalDto> });
  const combinedItems = React.useMemo(getCombinedItems, [processedFields, cachedData?.texts]);

  const handleDataChange = React.useCallback((dataToUpdate: Partial<QuestionnaireConsentTabUpdateDto> | Partial<QuestionnaireConsentTabCreateDto>) => {
    if (cachedData) {
      onChange({
        ...cachedData,
        ...dataToUpdate,
      });
    }
  }, [cachedData, onChange]);


  const handleUseSkipLogicChange = React.useCallback((val: string) => {
    handleDataChange({
      skipLogicEnabled: JSON.parse(val)
    });
  }, [handleDataChange]);

  const handleSkipLogicConditionAdd = React.useCallback(({ conditions }: { conditions: QuestionnaireSkipLogicConditionCreateDto[] }) => {
    const familyPagesSkipLogic: Array<SkipLogicCondition> = [
      ...familyPagesSkipLogicConditions,
      ...conditions.map((condition) => ({ ...condition, id: generateId(), operator: SkipLogicConditionOperator.equals } as SkipLogicCondition))
    ];

    handleDataChange({
      skipLogic: familyPagesSkipLogic.map(({ id, ...condition }) => condition as QuestionnaireSkipLogicConditionListDto)
    });
    setFamilyPagesSkipLogicConditions(familyPagesSkipLogic);
  }, [handleDataChange, familyPagesSkipLogicConditions]);

  const handleSkipLogicChange = React.useCallback((familyPagesSkipLogic: SkipLogicCondition[]) => {
    handleDataChange({
      skipLogic: familyPagesSkipLogic.map(({ id, ...condition }) => condition as QuestionnaireSkipLogicConditionCreateDto)
    });
    setFamilyPagesSkipLogicConditions(familyPagesSkipLogic);
  }, [handleDataChange]);

  const openFieldChooser = React.useCallback(() => {
    setFieldChooserOpened(true);
  }, []);

  const closeFieldChooser = React.useCallback(() => {
    setFieldChooserOpened(false);
  }, []);

  const handleFieldAdd = React.useCallback(
    async ({ fields }: FieldSelectorOnChangeMultiple) => {
      const newFields: Array<QuestionnaireFieldAddDto | QuestionnaireFieldListDto> = [];
      
      await Promise.all(
        fields.map(async (field, fieldIndex) => {
          const hasSameField = cachedData?.fields?.some(
            (x) =>
              getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }) ===
              getUniqueFieldCombination({ id: field.fieldId, context: field.fieldContext, type: field.fieldTypeId })
          );

          if (hasSameField || !cachedData) {
            return;
          }

          try {
            const fullField = await getFhq4QuestionnaireField({
              id: field.fieldId,
              context: field.fieldContext,
              system: field.systemField,
            });
            
            const newField: QuestionnaireFieldAddDto | QuestionnaireFieldListDto = {
              progenyId: field.fieldId as number,
              name: field.fieldName,
              path: fullField.path || fullField.name,
              order: combinedItems.length + fieldIndex + 1,
              systemField: field.systemField,
              context: field.fieldContext,
              type: fullField.type,
              progenyFieldType: field.fieldTypeId as FieldType,
              description: fullField.description,
              required: fullField.required,
              readonly: fullField.readonly,
              defaultValue: fullField.defaultValue,
              allowDuplications: fullField.allowDuplications,
              skipLogic: fullField.skipLogic,
              options: fullField.options,
              subfields: fullField.subfields,
              skipLogicEnabled: fullField.skipLogicEnabled,
              displayInFHQ: fullField.displayInFHQ,
              possibleDropdownOptions: fullField.possibleDropdownOptions,
            };

            newFields.push(newField);
          } catch (err) {
            console.error(err);
            showErrorMessage(`${I18n.t("Failed to get field with fieldName", { fieldName: field.fieldName })}`);
          }
        })
      );

      if (!newFields.length) {
        return;
      }

      handleDataChange({
        fields: [...(cachedData?.fields || []), ...newFields],
      });
    },
    [cachedData, handleDataChange, showErrorMessage, getFhq4QuestionnaireField, combinedItems]
  );

  const handleFieldsOrderChange = React.useCallback((newFieldsOrder: Array<FieldInList>) => {
    handleDataChange({
      fields: newFieldsOrder.map((x, i) => ({
        ...x.original,
        order: i + 1,
      }))
    });
  }, [handleDataChange]);

  const handleFieldDelete = React.useCallback((field: FieldInList) => {
    handleDataChange({
      fields: cachedData?.fields
        ?.filter((x) => 
          getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }) !== getUniqueFieldCombination({ id: field.original.progenyId, context: field.original.context, type: field.original.progenyFieldType })
        )
        .sort((a, b) => a.order - b.order)
        .map((f, i) => ({
          ...f,
          order: i + 1,
        }))
    });
  }, [cachedData, handleDataChange]);

  const handleFieldChange = React.useCallback((field: QuestionnaireFieldUpdateDto | QuestionnaireFieldAddDto) => {
    handleDataChange({
      fields: cachedData?.fields?.map((x) => 
        getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }) === getUniqueFieldCombination({ id: field.progenyId, context: field.context, type: field.progenyFieldType }) ? field : x
      ),
    });
  }, [cachedData, handleDataChange]);

  const navigateOnFieldEdit = React.useCallback((field: FieldInList) => {
    setEditingFieldUniqueCombination(getUniqueFieldCombination({ id: field.original.progenyId, context: field.original.context, type: field.original.progenyFieldType }));
  }, []);

  const goBackFromFieldEdit = React.useCallback(() => {
    setEditingFieldUniqueCombination(undefined);
  }, []);

  const canShowHideField = React.useCallback(() => {
    return false;
  }, []);

  const {
    editingTextBlock,
    addTextBlock,
    openTextBlockEditing,
    closeTextBlockEditing,
    handleTextBlockChange,
    handleTextBlockDelete,
    handleOrderChange,
  } = useTextBlock({
    data: cachedData,
    onChange: handleDataChange,
  });

  const handleAddTextBlock = React.useCallback(() => {
    addTextBlock({ startOrder: combinedItems.length });
  }, [addTextBlock, combinedItems]);

  const handleFieldsOrTextBlocksChange = React.useCallback((params: {
    texts: UpdatedLocalTexts<QuestionnaireTextBlockLocalDto>;
    fields: UpdatedLocalFields<FieldInList>;
}) => {
  handleDataChange({
      texts: params.texts,
      fields: params.fields.map(({order, ...field}) => ({ ...field.original, order }))
    })
  }, [handleDataChange]);

  return {
    processedFields,
    familyPagesSkipLogicConditions,
    handleSkipLogicChange,
    handleSkipLogicConditionAdd,
    handleUseSkipLogicChange,
    handleFieldChange,
    goBackFromFieldEdit,
    editingField,
    handleFieldAdd,
    openFieldChooser,
    closeFieldChooser,
    fieldChooserOpened,
    handleFieldsOrderChange,
    handleFieldDelete,
    navigateOnFieldEdit,
    canShowHideField,
    editingTextBlock,
    addTextBlock,
    handleOrderChange,
    handleTextBlockDelete,
    openTextBlockEditing,
    closeTextBlockEditing,
    handleTextBlockChange,
    handleFieldsOrTextBlocksChange,
    handleAddTextBlock,
    combinedItems
  };
};